<template>
  <div>
    <svg
      ref="svg"
      width="1000"
      height="700"
      viewBox="0 0 1000 700"
      preserveAspectRatio="xMidYMid meet"
      class="net-index-arrows"
    >
      <g v-for="connection in expressionConnections" :key="connection.id">
        <path
          :d="`M${connection.startPoint.x}, ${connection.startPoint.y} L${
            connection.endPoint.x
          },${connection.endPoint.y}`"
          class="net-index-arrows__line"
          :class="{ '-selected': selection.selected.has(connection.from.uid) || selection.selected.has(connection.to.uid) }"
        />
        <!-- arrowhead -->
        <!-- <circle
          :cx="connection.endPoint.x"
          :cy="connection.endPoint.y"
          r="4"
          class="net-index-arrows__anchor"
        /> -->
        <path
          v-if="connection.arrowPoint"
          :d="`M${connection.arrowPoint.x - 5},${connection.arrowPoint.y + 4} L${connection.arrowPoint.x},${connection.arrowPoint.y} L${connection.arrowPoint.x - 5},${connection.arrowPoint.y - 4}`"
          class="net-index-arrows__arrowhead"
          :class="{ '-selected': selection.selected.has(connection.from.uid) || selection.selected.has(connection.to.uid) }"
          :style="{ transform: `rotate(${
            Math.atan2(
              connection.arrowPoint.y - connection.startPoint.y,
              connection.arrowPoint.x - connection.startPoint.x
            ) * (180 / Math.PI)
          }deg)`, transformOrigin: `${connection.arrowPoint.x}px ${connection.arrowPoint.y}px` }"
        />
      </g>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { inject, computed } from "vue";
import { mapValues, pickBy } from "@/helpers/utility/CollectionFunctions";
import { notNull } from "@/helpers/utility/Type";
import {
  keyItemSizes,
  keyNetItems,
  keyNetCanvas,
} from "@editor/net/netCanvasInjectionKeys";
import { getAllReferencedItems } from "./indexFunctions";

import { useSceneTree } from "../scene/sceneTree";

import type { NetItems } from "@editor/net/netCanvasInjectionKeys";
import type { TargetItem } from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { IndexItem } from "./IndexItem";
import type { CanvasSelection } from "@editor/net/NetCanvasProps";
import type { SceneTreeItemSize } from "../scene/SceneTreeItem";
import type { RotatableRect } from "../scene/SceneNodeItem";
import type { FlowItem } from "../flow/FlowItem";

// interface Props {}
// defineProps<Props>();

const injectedNetItems = inject(keyNetItems);
const injectedNetCanvas = inject(keyNetCanvas);

const netItems = computed(
  () => injectedNetItems?.value || ({} as NetItems)
);
const selection = computed((): CanvasSelection => injectedNetCanvas?.value.props.selection || ({ selected: new Set<string>(), highlighted: new Set<string>(), active: new Set<string>() } as CanvasSelection));
const indices = computed(() => netItems.value.index || {});
const injectedItemSizes = inject(keyItemSizes);
const itemSizes = computed(() => injectedItemSizes?.value || {});
const sceneTree = useSceneTree();

// ====================
// INDICES

const visibleIndices = computed(() =>
  pickBy(indices.value, (index) => {
    const node = sceneTree.elementNodes.get(index.uid);
    if (!node) return false;
    return node.visible;
  })
);
const connectedItemsByIndex = computed(() =>
  mapValues(visibleIndices.value, (index) => {
    return new Set(
      getAllReferencedItems(index.valueExpression, netItems.value)
        .filter(notNull)
        .filter((item) => {
          const node = sceneTree.elementNodes.get(item.uid);
          if (!node) return false;
          return node.visible;
        })
    );
  })
);

// ====================
// FLOWS

const visibleFlows = computed<Record<number, FlowItem>>(() => {
  const flows = sceneTree.visibleFlows;
  const flowRecord: Record<number, FlowItem> = {};
  for (const flow of flows) {
    if (!itemSizes.value[flow.uid]) continue;
    flowRecord[flow.id] = flow;
  }
  return flowRecord;
});
const connectedItemsByFlow = computed(() =>
  mapValues(visibleFlows.value, (flow): Set<TargetItem | ResourceItem | IndexItem> => {
    if (flow.valueInputMode == "expression") {
      return new Set(
        getAllReferencedItems(flow.valueExpression, netItems.value)
          .filter(notNull)
          .filter((item) => {
            const node = sceneTree.elementNodes.get(item.uid);
            if (!node) return false;
            return node.visible;
          })
      );
    } else {
      if (flow.valueReferenceType == null || flow.valueReferenceId == null)
        return new Set();
      const referencedElement =
        netItems.value[flow.valueReferenceType]?.[flow.valueReferenceId];
      if (referencedElement == null) return new Set();
      return new Set([referencedElement]);
    }
  })
);

// ====================
// JOINED

type ExpressionConnection = {
  id: string;
  from: SceneTreeItemSize;
  to: SceneTreeItemSize;
  startPoint: { x: number; y: number };
  endPoint: { x: number; y: number };
  arrowPoint?: { x: number; y: number };
}

const expressionConnections = computed(() => {
  const connections: ExpressionConnection[] = [];

  for (const indexId in visibleIndices.value) {
    const index = visibleIndices.value[indexId];
    for (const item of connectedItemsByIndex.value[index.id]) {
      const fromNode = sceneTree.elementNodes.get(item.uid);
      const toNode = sceneTree.elementNodes.get(index.uid);
      if (!fromNode || !toNode) continue;
      const from = itemSizes.value[fromNode.uid];
      const to = itemSizes.value[toNode.uid];
      if (from == null || to == null) continue;
      const startPoint = getCirclePosition(from, to, 0);
      const arrowPoint = getCirclePosition(to, from, 10);
      const endPoint = getCirclePosition(to, from, 10);
      const id = `${item.uid}-${index.uid}`;
      connections.push({ id, from, to, startPoint, endPoint, arrowPoint });
    }
  }

  for (const flowId in visibleFlows.value) {
    const flow = visibleFlows.value[flowId];
    for (const item of connectedItemsByFlow.value[flow.id]) {
      const fromNode = sceneTree.elementNodes.get(item.uid);
      if (!fromNode) continue;
      const from = itemSizes.value[fromNode.uid];
      const to = itemSizes.value[flow.uid];
      if (from == null || to == null) continue;
      const startPoint = getCirclePosition(from, to, 0);
      const arrowPoint = getCirclePosition(to, from, 15);
      const endPoint = getCirclePosition(to, from, 10);
      const id = `${item.uid}-${flow.uid}`;
      connections.push({ id, from, to, startPoint, endPoint, arrowPoint });
    }
  }

  return connections;
});

// ====================
// FUNCTIONS

/**
 * Find the position for a circle on the line between two items, so that the circle is on the border of the first item.
 * @param index - The item the line is starting from. The circle will be on the border of this item.
 * @param item - The item the line is ending at. This item only defines the direction of the line.
 */
function getCirclePosition(index: RotatableRect, item: RotatableRect, padding = 2) {
  if (index == null || item == null) return { x: 0, y: 0 };
  const centerA = {
    x: index.x + index.width / 2,
    y: index.y + index.height / 2
  };
  const centerB = {
    x: item.x + item.width / 2,
    y: item.y + item.height / 2
  };
  return lineIntersectionOnRect(
    index.width + padding,
    index.height + padding,
    centerA.x,
    centerA.y,
    centerB.x,
    centerB.y
  );
}

/**
 * Finds the intersection point between
 *   * a rectangle centered in point B
 *     with sides parallel to the x and y axes
 *   * a line passing through points A and B (the center of the rectangle)
 *
 * @param width: rectangle width
 * @param height: rectangle height
 * @param xB; rectangle center x coordinate
 * @param yB; rectangle center y coordinate
 * @param xA; point A x coordinate
 * @param yA; point A y coordinate
 * @author Federico Destefanis
 * @see <a href="https://stackoverflow.com/a/31254199/2668213">based on</a>
 */

function lineIntersectionOnRect(
  width: number,
  height: number,
  xB: number,
  yB: number,
  xA: number,
  yA: number
) {
  let w = width / 2;
  let h = height / 2;
  if (w == 0 || h == 0) return { x: xB, y: yB };

  let dx = xA - xB;
  let dy = yA - yB;

  // if A=B return B itself
  if (dx == 0 && dy == 0)
    return {
      x: xB,
      y: yB
    };

  let tan_phi = h / w;
  let tan_theta = Math.abs(dy / dx);

  // tell me in which quadrant the A point is
  let qx = Math.sign(dx);
  let qy = Math.sign(dy);

  let xI, yI;

  if (tan_theta > tan_phi) {
    xI = xB + (h / tan_theta) * qx;
    yI = yB + h * qy;
  } else {
    xI = xB + w * qx;
    yI = yB + w * tan_theta * qy;
  }

  return {
    x: xI,
    y: yI
  };
}
</script>

<style lang="scss">
.net-index-arrows {
  position: absolute;
  top: 0;
  left: 0;
  overflow: visible;

  &__line {
    stroke-width: 0.5px;
    stroke: var(--color-text);
    stroke: color-mix(in srgb, var(--color-text), var(--color-background-deep) 50%);


    &.-selected {
      stroke: color-mix(in srgb, var(--color-primary), var(--color-background-deep) 50%);
      stroke-width: 2px;
    }
  }
  &__anchor {
    fill: var(--color-text);
  }
  &__arrowhead {
    fill: transparent;
    stroke: var(--color-text);
    stroke: color-mix(in srgb, var(--color-text), var(--color-background-deep) 50%);

    &.-selected {
      stroke: color-mix(in srgb, var(--color-primary), var(--color-background-deep) 50%);
      stroke-width: 1.5px;
    }
  }
}
</style>
